/**
 * useTelegram — Paritaets-Runde 3 / Stream 2 (Shared-Bot-Flow)
 *
 * GET    /api/v2/account/telegram             -> TelegramSettings
 * PATCH  /api/v2/account/telegram             -> TelegramSettings (flacher Body)
 * DELETE /api/v2/account/telegram             -> TelegramSettings (Disconnect)
 * POST   /api/v2/account/telegram/regenerate  -> TelegramSettings (neuer Token)
 * POST   /api/v2/account/telegram/test        -> { ok, message }
 *
 * Verbindungsmodell = v1: EIN globaler Bot, der User schickt seinen
 * Connect-Token an den Bot (Deep-Link), der Bot setzt die chat_id.
 * `bot_token` (Bring-your-own-Bot) bleibt optionaler Advanced-Zusatz;
 * gelesen wird nur noch das Boolean `bot_token_set`.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import i18n from '../../i18n'

// /api/v2/account/telegram* ist (noch) nicht in @heliumtracker/api-types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const client = apiClient as any

export type TelegramSettings = {
  connect_token: string | null
  connect_token_masked: string | null
  bot_username: string | null
  deep_link: string | null
  /** BE liefert die Telegram-Chat-ID als Integer; aeltere Staende als String. */
  chat_id: string | number | null
  connected: boolean
  enabled: boolean
  muted_until: string | null
  notify_income: boolean
  notify_activity: boolean
  /** Bring-your-own-Bot: ist ein eigener Bot-Token hinterlegt? */
  bot_token_set: boolean
  /** Alias aus dem wiederhergestellten BE-Contract (Boolean, nie der Klartext). */
  bot_token?: boolean
  verified_at: string | null
}

export type TelegramSavePayload = {
  enabled?: boolean
  muted_until?: string | null
  notify_income?: boolean
  notify_activity?: boolean
  /** Advanced / Bring-your-own-Bot */
  bot_token?: string | null
}

async function fetchTelegramSettings(): Promise<TelegramSettings | null> {
  const result = await client.GET('/api/v2/account/telegram')
  if (result.error) {
    throw new Error('Failed to load Telegram settings')
  }
  return (result.data ?? null) as TelegramSettings | null
}

async function updateTelegramSettings(payload: TelegramSavePayload): Promise<TelegramSettings> {
  const result = await client.PATCH('/api/v2/account/telegram', { body: payload })
  if (result.error) {
    throw new Error(i18n.t('settings:telegram.saveError'))
  }
  return result.data as TelegramSettings
}

async function disconnectTelegram(): Promise<TelegramSettings> {
  const result = await client.DELETE('/api/v2/account/telegram')
  if (result.error) {
    throw new Error(i18n.t('settings:telegram.disconnectError'))
  }
  return result.data as TelegramSettings
}

async function regenerateTelegramToken(): Promise<TelegramSettings> {
  const result = await client.POST('/api/v2/account/telegram/regenerate')
  if (result.error) {
    throw new Error(i18n.t('settings:telegram.regenerateError'))
  }
  return result.data as TelegramSettings
}

async function sendTestMessage(): Promise<{ ok: boolean; message: string }> {
  const result = await client.POST('/api/v2/account/telegram/test')
  if (result.error) {
    throw new Error(i18n.t('settings:telegram.testError'))
  }
  return result.data as { ok: boolean; message: string }
}

export const TELEGRAM_QUERY_KEY = ['account', 'telegram'] as const

export function useTelegram() {
  const queryClient = useQueryClient()

  const query = useQuery<TelegramSettings | null>({
    queryKey: TELEGRAM_QUERY_KEY,
    queryFn: fetchTelegramSettings,
    retry: false,
    staleTime: 60_000,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: TELEGRAM_QUERY_KEY })
    // Telegram-Verfuegbarkeit gated die Notification-Matrix (Legacy + Story 3.4).
    queryClient.invalidateQueries({ queryKey: ['account', 'notification_settings'] })
    queryClient.invalidateQueries({ queryKey: ['notifications', 'matrix'] })
  }

  const updateMutation = useMutation({
    mutationFn: (payload: TelegramSavePayload) => updateTelegramSettings(payload),
    onSuccess: invalidate,
  })

  const disconnectMutation = useMutation({
    mutationFn: disconnectTelegram,
    onSuccess: invalidate,
  })

  const regenerateMutation = useMutation({
    mutationFn: regenerateTelegramToken,
    onSuccess: invalidate,
  })

  const testMutation = useMutation({
    mutationFn: sendTestMessage,
    onSuccess: (data) => {
      if (data.ok) invalidate()
    },
  })

  return {
    settings: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
    updateSettings: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    disconnect: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
    regenerateToken: regenerateMutation.mutateAsync,
    isRegenerating: regenerateMutation.isPending,
    sendTestMessage: testMutation.mutateAsync,
    isSendingTest: testMutation.isPending,
  }
}
